import { PageSectionProps } from "@components/page-section/page-section";
import { useDateTime } from "@hooks/use-date-time";
import { SubscriptionInfoItem } from "@pages/settings/subscription-settings/subscription-info-item";
import { SubscriptionInfoSubtitle } from "@pages/settings/subscription-settings/subscription-info-subtitle";
import { DEFAULT_SUBSCRIPTION_NAME_NO_SUBSCRIPTION } from "@src/constants/subscription-constants";
import { activeSubscriptionSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { useMemo } from "react";

interface UseSubscriptionSections {
  /** Props for the subscription section */
  subscriptionSectionProps: PageSectionProps[];
}

/** Custom hook to return subscription settings props for subscription page */
export function useSubscriptionSettings(): UseSubscriptionSections {
  const { formatDate } = useDateTime();

  const activeSubscription = useAppSelector(activeSubscriptionSelector);

  // Props for the subscription section
  const subscriptionSectionProps: PageSectionProps[] = useMemo(() => {
    if (!activeSubscription) {
      return [];
    }

    return [
      {
        title: "Current plan",
        subtitle: <SubscriptionInfoSubtitle name={activeSubscription.label} />,
        items: [
          {
            label: "Subscription plan",
            shouldAddRightPadding: true,
            content: (
              <SubscriptionInfoItem
                text={activeSubscription.label ?? DEFAULT_SUBSCRIPTION_NAME_NO_SUBSCRIPTION}
                // Do not translate if we have a label for the active subscription
                shouldNotTranslate={!!activeSubscription.label}
              />
            ),
          },
          {
            label: "End date",
            shouldAddRightPadding: true,
            isHidden: !activeSubscription.endDate,
            content: (
              <SubscriptionInfoItem
                // The extra check for endDate is needed to satisfy TypeScript, because the content won't be shown if undefined
                text={activeSubscription.endDate ? formatDate(activeSubscription.endDate) : ""}
                shouldNotTranslate
              />
            ),
          },
        ],
      },
    ];
  }, [activeSubscription, formatDate]);

  return { subscriptionSectionProps };
}
